import { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import ProtectedRoute from '../components/feature/ProtectedRoute';

// Lazy load pages
const HomePage = lazy(() => import('../pages/home/page'));
const FormationsPage = lazy(() => import('../pages/formations/page'));
const FormationDetailPage = lazy(() => import('../pages/formations/detail/page'));
const BlogPage = lazy(() => import('../pages/blog/page'));
const BlogDetailPage = lazy(() => import('../pages/blog/detail/page'));
const ContactPage = lazy(() => import('../pages/contact/page'));
const AProposPage = lazy(() => import('../pages/a-propos/page'));
const GaleriePage = lazy(() => import('../pages/galerie/page'));
const TemoignagesPage = lazy(() => import('../pages/temoignages/page'));
const FAQPage = lazy(() => import('../pages/faq/page'));
const FormateurDetailPage = lazy(() => import('../pages/formateurs/detail/page'));
const FormateursPage = lazy(() => import('../pages/formateurs/page'));
const CataloguePage = lazy(() => import('../pages/catalogue/page'));
const MentionsLegalesPage = lazy(() => import('../pages/mentions-legales/page'));
const PolitiqueConfidentialitePage = lazy(() => import('../pages/politique-confidentialite/page'));
const NotFoundPage = lazy(() => import('../pages/NotFound'));
const MonDevisPage = lazy(() => import('../pages/mon-devis/page'));
const InscriptionPage = lazy(() => import('../pages/inscription/page'));
const ConnexionPage = lazy(() => import('../pages/connexion/page'));
const MotDePasseOubliePage = lazy(() => import('../pages/mot-de-passe-oublie/page'));
const ReinitialiserMotDePassePage = lazy(() => import('../pages/reinitialiser-mot-de-passe/page'));
const PaiementConfirmePage = lazy(() => import('../pages/paiement-confirme/page'));
const InscriptionConfirmeePage = lazy(() => import('../pages/inscription-confirmee/page'));
const CreerComptePage = lazy(() => import('../pages/creer-compte/page'));

// Dashboard pages
const AdminDashboardPage = lazy(() => import('../pages/admin/dashboard/page'));
const AdminFormationsPage = lazy(() => import('../pages/admin/formations/page'));
const AdminParticipantsPage = lazy(() => import('../pages/admin/participants/page'));
const AdminFormateursPage = lazy(() => import('../pages/admin/formateurs/page'));
const AdminDevisPage = lazy(() => import('../pages/admin/devis/page'));
const AdminFinancesPage = lazy(() => import('../pages/admin/finances/page'));
const AdminParametresPage = lazy(() => import('../pages/admin/parametres/page'));
const FormateurDashboardPage = lazy(() => import('../pages/formateur/dashboard/page'));
const ParticipantDashboardPage = lazy(() => import('../pages/participant/dashboard/page'));
const ParticipantInscriptionsPage = lazy(() => import('../pages/participant/inscriptions/page'));
const ParticipantFavorisPage = lazy(() => import('../pages/participant/favoris/page'));
const ParticipantCertificatsPage = lazy(() => import('../pages/participant/certificats/page'));
const ParticipantDevisPage = lazy(() => import('../pages/participant/devis/page'));
const ParticipantProfilPage = lazy(() => import('../pages/participant/profil/page'));
const ParticipantPaiementsPage = lazy(() => import('../pages/participant/paiements/page'));
const ParticipantMessagériePage = lazy(() => import('../pages/participant/messagerie/page'));
const ParticipantInscriptionDetailPage = lazy(() => import('../pages/participant/inscription-detail/page'));
const ParticipantFormationVideoPage = lazy(() => import('../pages/participant/formation-video/page'));
const ParticipantMesFormationsPage = lazy(() => import('../pages/participant/mes-formations/page'));

const FormateurFormationsPage = lazy(() => import('../pages/formateur/formations/page'));
const FormateurSessionsPage = lazy(() => import('../pages/formateur/sessions/page'));
const FormateurEmargementPage = lazy(() => import('../pages/formateur/emargement/page'));
const FormateurEvaluationsPage = lazy(() => import('../pages/formateur/evaluations/page'));
const FormateurProfilPage = lazy(() => import('../pages/formateur/profil/page'));
const FormateurSupportsPage = lazy(() => import('../pages/formateur/supports/page'));
const FormateurRessourcesPage = lazy(() => import('../pages/formateur/ressources/page'));

const AdminSessionsPage = lazy(() => import('../pages/admin/sessions/page'));
const AdminUtilisateursPage = lazy(() => import('../pages/admin/utilisateurs/page'));
const AdminPaiementsPage = lazy(() => import('../pages/admin/paiements/page'));
const AdminContenusPage = lazy(() => import('../pages/admin/contenus/page'));
const AdminRappelsPage = lazy(() => import('../pages/admin/rappels/page'));
const AdminMessagesPage = lazy(() => import('../pages/admin/messages/page'));
const AdminInscriptionsPage = lazy(() => import('../pages/admin/inscriptions/page'));
const AdminStatistiquesPage = lazy(() => import('../pages/admin/statistiques/page'));
const AdminStripeSessionsPage = lazy(() => import('../pages/admin/stripe-sessions/page'));
const AdminCertificatsPage = lazy(() => import('../pages/admin/certificats/page'));
const AdminPaiementsManuelsPage = lazy(() => import('../pages/admin/paiements-manuels/page'));
const AdminInscriptionDetailPage = lazy(() => import('../pages/admin/inscription-detail/page'));
const AdminStatistiquesFinancieresPage = lazy(() => import('../pages/admin/statistiques-financieres/page'));
const AdminFormationChapitresPage = lazy(() => import('../pages/admin/formation-chapitres/page'));

const routes: RouteObject[] = [
  {
    path: '/',
    element: <HomePage />,
  },
  {
    path: '/formations',
    element: <FormationsPage />,
  },
  {
    path: '/formations/:id',
    element: <FormationDetailPage />,
  },
  {
    path: '/catalogue',
    element: <CataloguePage />,
  },
  {
    path: '/blog',
    element: <BlogPage />,
  },
  {
    path: '/blog/:id',
    element: <BlogDetailPage />,
  },
  {
    path: '/contact',
    element: <ContactPage />,
  },
  {
    path: '/a-propos',
    element: <AProposPage />,
  },
  {
    path: '/galerie',
    element: <GaleriePage />,
  },
  {
    path: '/temoignages',
    element: <TemoignagesPage />,
  },
  {
    path: '/faq',
    element: <FAQPage />,
  },
  {
    path: '/formateurs',
    element: <FormateursPage />,
  },
  {
    path: '/formateurs/:id',
    element: <FormateurDetailPage />,
  },
  {
    path: '/mentions-legales',
    element: <MentionsLegalesPage />,
  },
  {
    path: '/politique-confidentialite',
    element: <PolitiqueConfidentialitePage />,
  },
  {
    path: '/mon-devis',
    element: <MonDevisPage />,
  },
  {
    path: '/inscription',
    element: <InscriptionPage />,
  },
  {
    path: '/connexion',
    element: <ConnexionPage />,
  },
  {
    path: '/mot-de-passe-oublie',
    element: <MotDePasseOubliePage />,
  },
  {
    path: '/reinitialiser-mot-de-passe',
    element: <ReinitialiserMotDePassePage />,
  },
  {
    path: '/paiement-confirme',
    element: <PaiementConfirmePage />,
  },
  {
    path: '/inscription-confirmee',
    element: <InscriptionConfirmeePage />,
  },
  {
    path: '/creer-compte',
    element: <CreerComptePage />,
  },
  // Protected dashboard routes - Admin
  {
    path: '/admin',
    element: <Navigate to="/admin/dashboard" replace />,
  },
  {
    path: '/admin/dashboard',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminDashboardPage /></ProtectedRoute>,
  },
  {
    path: '/admin/formations',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminFormationsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/participants',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminParticipantsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/formateurs',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminFormateursPage /></ProtectedRoute>,
  },
  {
    path: '/admin/devis',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminDevisPage /></ProtectedRoute>,
  },
  {
    path: '/admin/finances',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminFinancesPage /></ProtectedRoute>,
  },
  {
    path: '/admin/parametres',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminParametresPage /></ProtectedRoute>,
  },
  {
    path: '/admin/sessions',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminSessionsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/utilisateurs',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminUtilisateursPage /></ProtectedRoute>,
  },
  {
    path: '/admin/paiements',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminPaiementsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/contenus',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminContenusPage /></ProtectedRoute>,
  },
  {
    path: '/admin/rappels',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminRappelsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/messages',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminMessagesPage /></ProtectedRoute>,
  },
  {
    path: '/admin/inscriptions',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminInscriptionsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/inscriptions/:id',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminInscriptionDetailPage /></ProtectedRoute>,
  },
  {
    path: '/admin/statistiques',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminStatistiquesPage /></ProtectedRoute>,
  },
  {
    path: '/admin/statistiques-financieres',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminStatistiquesFinancieresPage /></ProtectedRoute>,
  },
  {
    path: '/admin/stripe-sessions',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminStripeSessionsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/certificats',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminCertificatsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/paiements-manuels',
    element: <ProtectedRoute allowedRoles={['admin', 'financier']}><AdminPaiementsManuelsPage /></ProtectedRoute>,
  },
  {
    path: '/admin/formation-chapitres',
    element: <ProtectedRoute allowedRoles={['admin']}><AdminFormationChapitresPage /></ProtectedRoute>,
  },
  // Protected dashboard routes - Formateur
  {
    path: '/formateur',
    element: <Navigate to="/formateur/dashboard" replace />,
  },
  {
    path: '/formateur/dashboard',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurDashboardPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/formations',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurFormationsPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/sessions',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurSessionsPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/emargement',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurEmargementPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/evaluations',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurEvaluationsPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/supports',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurSupportsPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/ressources',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurRessourcesPage /></ProtectedRoute>,
  },
  {
    path: '/formateur/profil',
    element: <ProtectedRoute allowedRoles={['formateur', 'admin']}><FormateurProfilPage /></ProtectedRoute>,
  },
  // Protected dashboard routes - Participant
  {
    path: '/participant',
    element: <Navigate to="/participant/dashboard" replace />,
  },
  {
    path: '/participant/dashboard',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantDashboardPage /></ProtectedRoute>,
  },
  {
    path: '/participant/inscriptions',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantInscriptionsPage /></ProtectedRoute>,
  },
  {
    path: '/participant/inscriptions/:id',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantInscriptionDetailPage /></ProtectedRoute>,
  },
  {
    path: '/participant/formation-video/:inscriptionId',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantFormationVideoPage /></ProtectedRoute>,
  },
  {
    path: '/participant/mes-formations',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantMesFormationsPage /></ProtectedRoute>,
  },
  {
    path: '/participant/favoris',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantFavorisPage /></ProtectedRoute>,
  },
  {
    path: '/participant/certificats',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantCertificatsPage /></ProtectedRoute>,
  },
  {
    path: '/participant/devis',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantDevisPage /></ProtectedRoute>,
  },
  {
    path: '/participant/profil',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantProfilPage /></ProtectedRoute>,
  },
  {
    path: '/participant/paiements',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantPaiementsPage /></ProtectedRoute>,
  },
  {
    path: '/participant/messagerie',
    element: <ProtectedRoute allowedRoles={['participant', 'admin']}><ParticipantMessagériePage /></ProtectedRoute>,
  },
  {
    path: '*',
    element: <NotFoundPage />,
  },
];

export default routes;